/**
 * Dependency validation and fallback helper
 * Provides centralized dependency checking with fallback to globals when needed
 */

import type { DependencyMap } from '../types/dependencies.types';

export class DependencyHelper {
  /**
   * Maps dependency names to their global variable names
   */
  private static globalMap: DependencyMap = {
    userRepository: 'UserRepository',
    statsig: 'StatsigManager',
    statsManager: 'StatsManager',
    config: 'CONFIG',
    consentManager: 'ConsentManager',
    metaPixel: 'MetaPixelManager',
    apphudPixel: 'ApphudPixelManager',
    profitWellPixel: 'ProfitWellPixel',
    flowManager: 'FlowManager',
    galleryManager: 'GalleryManager',
    paywallRenderer: 'PaywallRenderer',
    paddleCheckout: 'PaddleCheckout',
    singersCarousel: 'SingersCarousel',
  };

  /**
   * Validates dependencies and attempts fallback to global if null
   * @param deps - The dependencies object to validate
   * @param required - Array of required dependency names
   * @param moduleName - Name of the module requesting validation (for error messages)
   * @returns Fixed dependencies object
   */
  static validateAndFixDependencies<T extends Record<string, any>>(
    deps: Partial<T> = {},
    required: string[] = [],
    moduleName: string = 'Unknown'
  ): T {
    const fixedDeps: Record<string, any> = {};
    const issues: string[] = [];

    // Check each required dependency
    required.forEach((key) => {
      const depValue = deps[key as keyof T];

      if (depValue === null || depValue === undefined) {
        // Attempt to find global fallback
        const globalName = this.getGlobalName(key);

        if (globalName && typeof (window as any)[globalName] !== 'undefined') {
          console.warn(
            `[${moduleName}] Dependency '${key}' was null, falling back to global ${globalName}`
          );
          fixedDeps[key] = (window as any)[globalName];
        } else {
          issues.push(key);
        }
      } else {
        fixedDeps[key] = depValue;
      }
    });

    // Throw error if any dependencies couldn't be resolved
    if (issues.length > 0) {
      const message = `[${moduleName}] Missing required dependencies that could not be resolved: ${issues.join(', ')}`;
      console.error(message);
      throw new Error(message);
    }

    return fixedDeps as T;
  }

  /**
   * Gets the global name for a dependency key
   */
  private static getGlobalName(depKey: string): string | undefined {
    return this.globalMap[depKey];
  }

  /**
   * Checks if a specific dependency is available (non-null)
   */
  static isDependencyAvailable(deps: Record<string, any>, depName: string): boolean {
    return deps && deps[depName] !== null && deps[depName] !== undefined;
  }

  /**
   * Gets a dependency safely with optional chaining
   */
  static getDependency<T>(
    deps: Record<string, any>,
    depName: string,
    fallbackValue: T | null = null
  ): T | null {
    if (this.isDependencyAvailable(deps, depName)) {
      return deps[depName] as T;
    }

    // Try global fallback
    const globalName = this.getGlobalName(depName);
    if (globalName && typeof (window as any)[globalName] !== 'undefined') {
      console.warn(`Using global fallback for ${depName}: ${globalName}`);
      return (window as any)[globalName] as T;
    }

    return fallbackValue;
  }

  /**
   * Registers a custom global mapping
   */
  static registerGlobalMapping(depKey: string, globalName: string): void {
    this.globalMap[depKey] = globalName;
  }
}

export default DependencyHelper;
